import React from "react";
import { describeActivity, initials, splitMentions } from "./taskUtils";

// Consecutive messages from one person within this window share a header.
const GROUP_WINDOW_MS = 5 * 60 * 1000;

function dayLabel(date) {
  const key = date.toLocaleDateString("en-CA");
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (key === today.toLocaleDateString("en-CA")) return "Today";
  if (key === yesterday.toLocaleDateString("en-CA")) return "Yesterday";
  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

function timeLabel(date) {
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function MessageBody({ body }) {
  return (
    <div className="tk-bubble-text">
      {splitMentions(body).map((part, idx) =>
        part.type === "mention" ? (
          <span key={idx} className="tk-mention">
            @{part.name}
          </span>
        ) : (
          <React.Fragment key={idx}>{part.value}</React.Fragment>
        ),
      )}
    </div>
  );
}

// Chat-style thread of a task's comments, optionally interleaved with its
// activity log as compact system lines.
export default function TaskChat({
  comments,
  activities,
  showActivity,
  currentUserId,
  canDelete,
  onDelete,
}) {
  const items = [
    ...comments.map((c) => ({
      kind: "comment",
      at: new Date(c.createdAt),
      item: c,
    })),
    ...(showActivity
      ? activities.map((a) => ({
          kind: "activity",
          at: new Date(a.createdAt),
          item: a,
        }))
      : []),
  ].sort((a, b) => a.at - b.at);

  if (items.length === 0) {
    return (
      <div className="tk-chat-empty">
        <p>No messages yet.</p>
        <p className="tk-muted">
          Start the conversation — type @ to bring someone into this task.
        </p>
      </div>
    );
  }

  const rows = [];
  let lastDay = null;
  let prev = null;
  for (const entry of items) {
    const day = entry.at.toLocaleDateString("en-CA");
    if (day !== lastDay) {
      rows.push(
        <div key={`day-${day}`} className="tk-chat-day" role="separator">
          <span>{dayLabel(entry.at)}</span>
        </div>,
      );
      lastDay = day;
      prev = null;
    }

    if (entry.kind === "activity") {
      const a = entry.item;
      rows.push(
        <div key={`a-${a.id}`} className="tk-chat-system">
          <span>
            <strong>{a.actor?.name}</strong> {describeActivity(a)} ·{" "}
            {timeLabel(entry.at)}
          </span>
        </div>,
      );
      prev = entry;
      continue;
    }

    const c = entry.item;
    const mine = c.author?.id === currentUserId;
    const continued =
      prev?.kind === "comment" &&
      prev.item.author?.id === c.author?.id &&
      entry.at - prev.at < GROUP_WINDOW_MS;

    rows.push(
      <div
        key={`c-${c.id}`}
        className={`tk-msg ${mine ? "mine" : ""} ${continued ? "continued" : ""}`.trim()}
      >
        {!mine && (
          <span className="tk-msg-avatar">
            {!continued && (
              <span className="tk-avatar">{initials(c.author?.name)}</span>
            )}
          </span>
        )}
        <div className="tk-msg-main">
          {!continued && (
            <div className="tk-msg-head">
              {!mine && <strong>{c.author?.name}</strong>}
              <span className="tk-muted">{timeLabel(entry.at)}</span>
            </div>
          )}
          <div className="tk-bubble" title={entry.at.toLocaleString("en-IN")}>
            <MessageBody body={c.body} />
          </div>
          {canDelete(c) && (
            <button
              type="button"
              className="tk-msg-delete"
              onClick={() => onDelete(c)}
              aria-label="Delete message"
            >
              Delete
            </button>
          )}
        </div>
      </div>,
    );
    prev = entry;
  }

  return <div className="tk-chat">{rows}</div>;
}
